import _ from 'lodash';
import React, {
  useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ErrorHandler, CenteredLoader, usePaginatedQuery } from '@playbookux/playbook-web-core';
import FilterTags from './filter-tags';
import withLayout from '../../../core/components/with-layout';
import CardsHighlights from '../../../core/components/cards-highlights';
import Tabs from '../../constants/tabs';
import useTags from '../../../core/hooks/use-tags';
import DateRangePicker from '../../../core/components/date-range-picker';
import BarChart from './bar-chart';

import {
  LIMIT,
  SKIP,
  TAG_ANALYTICS,
} from './graphql';

import {
  Card,
  CardHeader,
  CardsContainer,
  Container,
  Content,
  ContentLeftSide,
  ContentRightSide,
  FilterItem,
  FiltersContainer,
  Header,
  Link,
  Loader,
  Message,
  Text,
  TitleContainer,
  VideoContainer,
  ZeroState,
  FrameVideo,
  ProjectDropdownContainer,
} from './styles';
import ProjectsDropdown from '../../../core/components/projects-dropdown';

const ZERO_STATE_TEXT = 'You haven’t tagged anything yet. Watch this video to learn more about creating and analyzing tags.';

const assignGroupToHighlights = (tagHighlights, groupedTags, reactions) => {
  const ungroupedId = null;
  const ungroupedGroup = _.find(groupedTags, { id: ungroupedId });
  const reactionsMap = _.keyBy(reactions, 'key');

  return _.map(tagHighlights, (item) => {
    const tagHighlight = _.cloneDeep(item);
    const { tagGroup } = tagHighlight.tag;

    // assign ungrouped group
    if (!tagGroup) {
      tagHighlight.tag.tagGroup = ungroupedGroup;
    }

    // reactions
    const { tag } = tagHighlight;
    if (tag.tagType === 'reaction') {
      const { color } = reactionsMap[tag.name];
      tagHighlight.tag.tagGroup = {
        ...tagHighlight.tag.tagGroup,
        color,
      };
    }

    return tagHighlight;
  });
};

const Tags = (props) => {
  const { history, companyId } = props;

  const [selectedProjects, setSelectedProjects] = useState();
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTags, setSelectedTags] = useState();
  const [rangeDate, setRangeDate] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });

  const createdAtStart = rangeDate.startDate || null;
  const createdAtEnd = rangeDate.endDate || null;

  const skip = _.isEmpty(selectedTags);
  const selectedTagId = selectedTag && selectedTag.id;

  const {
    groupedAll: groupedTags,
    reactions,
    error: errorGroupedTags,
    loading: loadingUseTags,
  } = useTags(companyId) || {};

  const {
    loading: isLoading,
    error,
    data = {},
    hasMore,
    fetchMore,
    refetch,
  } = usePaginatedQuery(TAG_ANALYTICS, {
    variables: {
      companyId,
      tagIds: selectedTags,
      selectedTag: [selectedTagId],
      projectIds: selectedProjects,
      createdAtStart,
      createdAtEnd,
      limit: LIMIT,
      offset: SKIP,
    },
    skip,
  }, { dataField: 'tagHighlights' });

  const {
    tagHighlights = [],
    tagAnalytics = [],
  } = data;

  useEffect(() => {
    refetch.call();
  }, [selectedTag, selectedTags, refetch, selectedProjects]);

  const newTagHighlights = useMemo(() => (
    !_.isEmpty(tagHighlights) ? assignGroupToHighlights(tagHighlights, groupedTags, reactions) : tagHighlights
  ), [groupedTags, reactions, tagHighlights]);

  const orderedAnalytics = useMemo(() => (
    !_.isEmpty(tagAnalytics) ? _.orderBy(assignGroupToHighlights(tagAnalytics, groupedTags, reactions), ['totalPeriod'], ['desc']) : []
  ), [groupedTags, reactions, tagAnalytics]);

  const hasTagUsage = useMemo(() => {
    const totalTagHighlight = _.sumBy(groupedTags, 'countTagHighlight');
    return totalTagHighlight > 0;
  }, [groupedTags]);

  useEffect(() => {
    if (!loadingUseTags && !selectedTags && groupedTags && !_.isEmpty(groupedTags)) {
      setSelectedTags(_.flatMap(groupedTags, (v) => _.map(v.tags, 'id')));
    }
  }, [groupedTags, loadingUseTags, selectedTags]);

  useEffect(() => {
    const hasAnalytics = !_.isEmpty(orderedAnalytics) && !_.isEmpty(newTagHighlights);

    if (hasAnalytics && !isLoading) {
      const newSelectedTag = selectedTag && _.find(orderedAnalytics, { id: selectedTag.id });
      setSelectedTag(newSelectedTag || _.first(orderedAnalytics));
    }
  }, [isLoading, newTagHighlights, orderedAnalytics, selectedTag]);

  const handleClickManageTags = () => {
    history.push('/manage-tags');
  };

  if (!hasTagUsage && loadingUseTags) {
    return (
      <Container>
        <CenteredLoader />
      </Container>
    );
  }

  const errors = error || errorGroupedTags;

  if (errors) {
    return (
      <Container>
        <ErrorHandler
          error={errors}
          defaultMessage="Error while retrieving Tag Analytics."
        />
      </Container>
    );
  }

  if (!hasTagUsage && !loadingUseTags) {
    return (
      <Container>
        <ZeroState>
          <Text as="h1">
            {ZERO_STATE_TEXT}
          </Text>
          <VideoContainer>
            <FrameVideo
              src="https://player.vimeo.com/video/892332669"
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          </VideoContainer>
        </ZeroState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Link onClick={handleClickManageTags}>
          Manage tags
        </Link>
      </Header>

      <Content>
        <ContentLeftSide>
          <FilterTags
            groupedTags={groupedTags}
            selectedTags={selectedTags}
            onChange={setSelectedTags}
            loading={loadingUseTags}
          />
        </ContentLeftSide>

        <ContentRightSide>
          <Card>
            <CardHeader>
              <TitleContainer>
                <Text as="h3">
                  Frequency Count
                </Text>
                <Message>
                  Click on a bar to display highlights
                </Message>
              </TitleContainer>

              {isLoading && <Loader data-loader="tags-loader" />}

              <FiltersContainer>
                <FilterItem>
                  <DateRangePicker
                    onChange={setRangeDate}
                    rangeDate={rangeDate}
                    placeholder="Date filter"
                  />
                </FilterItem>
                <FilterItem>
                  <ProjectDropdownContainer>
                    <ProjectsDropdown
                      companyId={companyId}
                      setSelectedProjects={setSelectedProjects}
                    />
                  </ProjectDropdownContainer>
                </FilterItem>
              </FiltersContainer>
            </CardHeader>

            <BarChart
              onSelectedTag={setSelectedTag}
              tagAnalytics={orderedAnalytics}
              selectedTags={selectedTags}
              refetch={refetch}
            />
          </Card>

          {!_.isEmpty(newTagHighlights) && !_.isEmpty(selectedTag) && !isLoading && (
            <CardsContainer>
              <Text as="h3">
                {`Tag: ${selectedTag.tag.name} (${selectedTag.totalPeriod})`}
              </Text>

              <CardsHighlights
                hasMore={hasMore}
                highlights={newTagHighlights}
                onLoadMore={fetchMore}
                selectedTagId={selectedTag && selectedTag.id}
                typeHighlight="highlight"
              />
            </CardsContainer>
          )}
        </ContentRightSide>
      </Content>
    </Container>
  );
};

Tags.propTypes = {
  companyId: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  connect(mapStateToProps, null),
  withLayout({ selectedTab: Tabs.TAGS }),
);

export default enhance(Tags);
